/**
 * QuestionarioSanitarioPage — Quotatore RiS "RealmenteInSalute" (Reale Mutua).
 *
 * Wizard 6 step, NESSUN calcolo premio: la richiesta viene salvata come
 * lead nella collection `richieste_assicurative` (tipologia "rischi_salute")
 * e un consulente Reale Mutua (tramite Colucci) ricontatta entro 24h.
 *
 * VINCOLO GDPR/IVASS: il form NON raccoglie dati sanitari particolari
 * ex art. 9 GDPR. Solo flag generici di stile di vita (fumatore S/N,
 * ricoveri ultimi 5 anni S/N, terapie in corso S/N) — il dettaglio anamnesi
 * viene raccolto in fase emissione polizza dal consulente IVASS.
 */
import { useState, useEffect } from "react";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import CodiceFiscaleField from "@/components/common/CodiceFiscaleField";
import ComuneAutocomplete from "@/components/common/ComuneAutocomplete";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PrivacyLinks from "@/components/PrivacyLinks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { StepProgress, FormNavigation } from "@/components/StepProgress";
import AccountOptInCheckbox from "@/components/wizard/AccountOptInCheckbox";
import AccountCredentialsBox from "@/components/wizard/AccountCredentialsBox";
import { trackWizardSubmit, trackAccountCreated } from "@/lib/analytics";
import usePartnerTracking from "@/hooks/usePartnerTracking";
import IVASSComplianceCheckboxes, { buildMupVisioneMeta, isIvassComplianceOk } from "@/components/IVASSComplianceCheckboxes";
import ConsensiGranulari, { buildConsensiGranulariMeta, isConsensiOk } from "@/components/ConsensiGranulari";
import ContattoPreferenzeBlock from "@/components/ContattoPreferenzeBlock";
import AcceptAllInformative from "@/components/AcceptAllInformative";
import PrivacyFieldTooltip from "@/components/PrivacyFieldTooltip";
import {
  Heart, Sparkles, Shield, CheckCircle2, ArrowLeft, Stethoscope,
  Users as UsersIcon, Activity, FileText, AlertTriangle, ChevronRight,
  Info, ShieldCheck,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ["Anagrafica", "Nucleo", "Questionario sanitario", "Professione", "Prodotto", "Coperture", "Consensi"];
const STORAGE_KEY = "ris_wizard_v2";

const CATEGORIE_TERAPIE = [
  { id: "cardiovascolare", label: "Cardiovascolare" },
  { id: "metabolica", label: "Metabolica (diabete, tiroide)" },
  { id: "oncologica", label: "Oncologica" },
  { id: "neurologica", label: "Neurologica" },
  { id: "pneumologica", label: "Pneumologica" },
  { id: "altro", label: "Altro" },
];

const PROFESSIONI_COMUNI = ["Impiegato","Operaio","Medico","Insegnante","Imprenditore","Pensionato","Studente","Casalinga","Libero professionista","Altro"];
const SPORT_COMUNI = ["Calcio","Tennis","Sci","Equitazione","Motociclismo","Sub","Paracadutismo","Alpinismo","Trekking","Nuoto","Palestra","Corsa","Altro"];

function calcBMI(altezza_cm, peso_kg) {
  if (!altezza_cm || !peso_kg || altezza_cm <= 0 || peso_kg <= 0) return null;
  const h = altezza_cm / 100;
  const bmi = +(peso_kg / (h * h)).toFixed(1);
  let label = "—";
  if (bmi < 18.5) label = "Sottopeso";
  else if (bmi < 25) label = "Normopeso";
  else if (bmi < 30) label = "Sovrappeso";
  else if (bmi < 35) label = "Obesità classe I";
  else if (bmi < 40) label = "Obesità classe II";
  else label = "Obesità classe III";
  return { bmi, label };
}

const PROVINCE = [
  "AG","AL","AN","AO","AR","AP","AT","AV","BA","BT","BL","BN","BG","BI","BO","BZ","BS","BR","CA","CL","CB","CE","CT","CZ","CH","CO","CS","CR","KR","CN","EN","FM","FE","FI","FG","FC","FR","GE","GO","GR","IM","IS","SP","AQ","LT","LE","LC","LI","LO","LU","MC","MN","MS","MT","ME","MI","MO","MB","NA","NO","NU","OR","PD","PA","PR","PV","PG","PU","PE","PC","PI","PT","PN","PZ","PO","RG","RA","RC","RE","RI","RN","RM","RO","SA","SS","SV","SI","SR","SO","TA","TE","TR","TO","TP","TN","TV","TS","UD","VA","VE","VB","VC","VR","VV","VI","VT"
];

function Card({ children, ...props }) {
  return <div className="bg-white border border-[#E5E2DB] rounded-2xl p-5 sm:p-6 premium-card" {...props}>{children}</div>;
}

function OptionCard({ active, onClick, title, desc, badge, icon: Icon, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left rounded-2xl border-2 p-5 transition-all w-full ${active ? "border-[#163321] bg-[#163321]/5 shadow-lg" : "border-[#E5E2DB] bg-white hover:border-[#163321]/40"}`}
    >
      <div className="flex items-start gap-3">
        {Icon && (
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${active ? "bg-[#163321] text-white" : "bg-[#163321]/10 text-[#163321]"}`}>
            <Icon className="w-5 h-5" />
          </div>
        )}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-heading text-base font-medium text-[#0F1F14]">{title}</h3>
            {badge && <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#D97B58]/10 text-[#D97B58] border border-[#D97B58]/30">{badge}</span>}
          </div>
          <p className="text-xs text-[#4A5E51] leading-relaxed">{desc}</p>
        </div>
        {active && <CheckCircle2 className="w-5 h-5 text-[#163321] shrink-0" />}
      </div>
    </button>
  );
}

const initialState = {
  anagrafica: { nome: "", cognome: "", sesso: "", data_nascita: "", luogo_nascita: "", codice_fiscale: "", email: "", telefono: "", citta: "", provincia: "" },
  nucleo: { composizione: "singolo", coniuge_da_assicurare: false, figli: [] },
  stile_vita: { fumatore: false, altezza_cm: 170, peso_kg: 70 },
  professione: { professione: "", settore: "", lavora_estero: false, paese_estero: "", pratica_sport: false, sport_dettaglio: "", sport_professionistico: false },
  questionario_sanitario: {
    ha_patologie_dichiarate: null,
    fumatore: false,
    sport_agonistici_o_alto_rischio: false,
    colesterolo_oltre_250: false,
    pressione_arteriosa_elevata: false,
    invalidita_civile: false,
    invalidita_percentuale: null,
    sostanze_stupefacenti_alcol_problematico: false,
    esami_fuori_norma_in_attesa: false,
    ricoveri_ultimi_5_anni: null,
    terapie_continuative: null,
    terapie_categorie: [],
    interventi_chirurgici_importanti: null,
    patologia_cronica: null,
    patologia_cronica_categoria: [],
    dichiarazione_veridicita: false,
    consenso_art9_gdpr: false,
  },
  configurazione: {
    // Nuovo asse PRODOTTO (Fase 11C — 2026-05-15):
    //   "rsm"    → Rimborso Spese Mediche (Vita Intera Mod. 5170 / Durata Definita Mod. 5169)
    //   "target" → Formula Target Mod. 5168 (durata sempre annuale, sezioni indipendenti)
    prodotto_tipo: "rsm",
    // Solo se prodotto_tipo === "rsm":
    durata_rsm: "vita_intera",   // "annuale" | "poliennale" | "vita_intera"
    // Solo se prodotto_tipo === "target":
    target_sezioni: [],          // sottoinsieme di ["kids","dental","fisio"]
    // Legacy (retro-compat): mappato lato submit dal durata_rsm
    prodotto: "vita_intera",
    durata_anni: null,
    // Coperture RSM (Step 5):
    opzione: "comfort",
    garanzie_extra: [],
    opzione_extraricovero_selezionata: null,
    rete_extra: "network",
    franchigia_extra_eur: null,
    franchigia_rsm_id: "no_franchigia",
    franchigia_ricovero: false,
    sconto_salute: false,
    frazionamento: "annuale",
  },
  consensi: { privacy: false, dati_sanitari_informativa: false, commerciale: false, profilazione: false },
  // FASE K2 LEGACY iter 69 — Opt-in creazione account cliente area riservata
  crea_account_ok: false,
  note_cliente: "",
};

export default function QuestionarioSanitarioPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  // v1.56 (feb-2026) — Click-wrap IVASS: art. 1892 c.c. + MUP Reg. 40/2018
  const [signedDeclaration, setSignedDeclaration] = useState(false);
  const [mupSeen, setMupSeen] = useState(false);
  const [mupFilename, setMupFilename] = useState(null);
  // v1.66 (Fase 2 Privacy) — Consensi granulari GDPR (ramo salute: sanitari OBBLIGATORIO)
  const [consensi, setConsensi] = useState({});
  // v1.72.3 — Cross-wizard: preferenze ricontatto + accept-all informative
  const [preferenzeRicontatto, setPreferenzeRicontatto] = useState({ canale: "", fascia: "" });
  const handleAcceptAllInformative = () => {
    setSignedDeclaration(true);
    setMupSeen(true);
    setConsensi((prev) => ({ ...prev, marketing: true, profilazione: true, gruppo_partner: true }));
  };
  const sessionHashRIS = React.useMemo(
    () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`, []);
  // ITER 69 — partner tracking via hook DRY (`?partner=X&sede=Y&azienda=Z`)
  const { partnerId, sede, azienda } = usePartnerTracking();

  const [d, setD] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...initialState, ...JSON.parse(raw) };
    } catch {/* ignore */}
    return initialState;
  });

  // autosave (no consensi)
  useEffect(() => {
    try {
      const { consensi, ...rest } = d;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(rest));
    } catch {/* ignore */}
  }, [d]);

  const upd = (path, value) => {
    setD((prev) => {
      const next = { ...prev };
      const keys = path.split(".");
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) {
        ref[keys[i]] = { ...ref[keys[i]] };
        ref = ref[keys[i]];
      }
      ref[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const toggleGaranzia = (id) => {
    setD((p) => {
      const has = p.configurazione.garanzie_extra.includes(id);
      const list = has ? p.configurazione.garanzie_extra.filter((x) => x !== id) : [...p.configurazione.garanzie_extra, id];
      return { ...p, configurazione: { ...p.configurazione, garanzie_extra: list } };
    });
  };

  // Nuova Area Extra Ricovero (2026-05-09): scelta MUTUALMENTE ESCLUSIVA
  // tra le 3 opzioni. Sincronizza anche `garanzie_extra` (retro-compat).
  const selectOpzioneExtra = (id) => {
    setD((p) => {
      const current = p.configurazione.opzione_extraricovero_selezionata;
      const next = current === id ? null : id;
      return {
        ...p,
        configurazione: {
          ...p.configurazione,
          opzione_extraricovero_selezionata: next,
          garanzie_extra: next ? [next] : [],
          // Reset franchigia se cambio opzione/deseleziono
          franchigia_extra_eur: current === id ? null : p.configurazione.franchigia_extra_eur,
        },
      };
    });
  };

  const toggleTerapiaCategoria = (id) => {
    setD((p) => {
      const has = p.questionario_sanitario.terapie_categorie.includes(id);
      const list = has
        ? p.questionario_sanitario.terapie_categorie.filter((x) => x !== id)
        : [...p.questionario_sanitario.terapie_categorie, id];
      return { ...p, questionario_sanitario: { ...p.questionario_sanitario, terapie_categorie: list } };
    });
  };
  const togglePatologiaCronicaCategoria = (id) => {
    setD((p) => {
      const has = p.questionario_sanitario.patologia_cronica_categoria.includes(id);
      const list = has
        ? p.questionario_sanitario.patologia_cronica_categoria.filter((x) => x !== id)
        : [...p.questionario_sanitario.patologia_cronica_categoria, id];
      return { ...p, questionario_sanitario: { ...p.questionario_sanitario, patologia_cronica_categoria: list } };
    });
  };

  const updFiglio = (idx, eta) => {
    setD((p) => {
      const figli = [...p.nucleo.figli];
      figli[idx] = { eta: parseInt(eta || "0", 10) };
      return { ...p, nucleo: { ...p.nucleo, figli } };
    });
  };
  const addFiglio = () => setD((p) => ({ ...p, nucleo: { ...p.nucleo, figli: [...p.nucleo.figli, { eta: 0 }] } }));
  const rmFiglio = (idx) => setD((p) => ({ ...p, nucleo: { ...p.nucleo, figli: p.nucleo.figli.filter((_, i) => i !== idx) } }));

  // ====== Helper età/prodotto (Fase 11C — 2026-05-15) ======
  const calcEta = () => {
    if (!d.anagrafica.data_nascita) return null;
    return Math.floor((Date.now() - new Date(d.anagrafica.data_nascita)) / (365.25 * 24 * 3600 * 1000));
  };

  const setProdottoTipo = (tipo) => {
    // Quando l'utente cambia asse prodotto resetta il sub-stato per evitare incongruenze.
    setD((p) => ({
      ...p,
      configurazione: {
        ...p.configurazione,
        prodotto_tipo: tipo,
        // Se ritorna su RSM e durata_rsm è vuota, default vita_intera (verrà disabled se età>60)
        durata_rsm: tipo === "rsm" ? (p.configurazione.durata_rsm || "vita_intera") : null,
        target_sezioni: tipo === "target" ? p.configurazione.target_sezioni : [],
        // Mantieni legacy `prodotto` allineato per il backend RiS esistente
        prodotto: tipo === "rsm"
          ? (p.configurazione.durata_rsm === "vita_intera" ? "vita_intera" : "durata_definita")
          : "target",
      },
    }));
  };

  const setDurataRsm = (durata) => {
    setD((p) => ({
      ...p,
      configurazione: {
        ...p.configurazione,
        durata_rsm: durata,
        // Legacy
        prodotto: durata === "vita_intera" ? "vita_intera" : "durata_definita",
        // Per Annuale/Poliennale resetto eventuale durata_anni custom
        durata_anni: durata === "vita_intera" ? null : (p.configurazione.durata_anni || (durata === "annuale" ? 1 : 5)),
      },
    }));
  };

  const toggleTargetSezione = (sez) => {
    setD((p) => {
      const cur = p.configurazione.target_sezioni;
      const has = cur.includes(sez);
      const next = has ? cur.filter((x) => x !== sez) : [...cur, sez];
      return { ...p, configurazione: { ...p.configurazione, target_sezioni: next } };
    });
  };


  // ====== Validazioni step ======
  const stepValid = () => {
    if (step === 0) {
      const a = d.anagrafica;
      if (!a.nome || !a.cognome || !a.sesso || !a.data_nascita) return "Compila tutti i campi anagrafica";
      if (!/^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/i.test(a.codice_fiscale)) return "Codice fiscale non valido (16 caratteri)";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.email)) return "Email non valida";
      if (!/^[\+\d\s\-\(\)]{6,20}$/.test(a.telefono)) return "Telefono non valido";
      if (!a.citta || !a.provincia) return "Inserisci città e provincia";
      // età ingresso indicativa: il limite Vita Intera (60) ora è gestito a Step 4 (Prodotto).
      // Qui blocco solo permanenza assoluta (75 anni — sia Durata Definita che Target sono off oltre).
      const dn = new Date(a.data_nascita);
      const eta = (Date.now() - dn) / (365.25 * 24 * 3600 * 1000);
      if (eta >= 75) return "Età ≥ 75: oltre il limite di permanenza in assicurazione (PDF Tariffa pag. 11).";
    }
    if (step === 1) {
      if (d.nucleo.composizione === "famiglia" && d.nucleo.figli.length === 0) return "Aggiungi almeno un figlio o cambia composizione del nucleo";
    }
    if (step === 2) {
      const sv = d.stile_vita;
      if (!sv.altezza_cm || sv.altezza_cm < 120) return "Indica un'altezza valida (min 120 cm)";
      if (!sv.peso_kg || sv.peso_kg < 25) return "Indica un peso valido (min 25 kg)";
      const q = d.questionario_sanitario;
      if (q.ha_patologie_dichiarate === null) return "Rispondi alla domanda iniziale del questionario sanitario";
      if (q.invalidita_civile && (q.invalidita_percentuale === null || q.invalidita_percentuale === "")) return "Inserisci la percentuale di invalidità civile";
      if (q.ha_patologie_dichiarate === true) {
        if (q.ricoveri_ultimi_5_anni === null) return "Indica se hai avuto ricoveri ospedalieri negli ultimi 5 anni";
        if (q.terapie_continuative === null) return "Indica se stai seguendo terapie continuative";
        if (q.terapie_continuative === true && q.terapie_categorie.length === 0) return "Seleziona almeno una categoria di terapia in corso";
        if (q.interventi_chirurgici_importanti === null) return "Indica se hai avuto interventi chirurgici importanti";
        if (q.patologia_cronica === null) return "Indica se hai una patologia cronica diagnosticata";
        if (q.patologia_cronica === true && q.patologia_cronica_categoria.length === 0) return "Seleziona almeno una categoria di patologia cronica";
        if (!q.dichiarazione_veridicita) return "Devi sottoscrivere la dichiarazione di veridicità (artt. 1892-1893 c.c.)";
      }
    }
    if (step === 3) {
      const p = d.professione;
      if (!p.professione || p.professione.length < 2) return "Indica la tua professione";
      if (p.lavora_estero && (!p.paese_estero || p.paese_estero.length < 2)) return "Indica il paese estero in cui lavori";
      if (p.pratica_sport && (!p.sport_dettaglio || p.sport_dettaglio.length < 2)) return "Indica quale sport pratichi";
    }
    if (step === 4) {
      // Step Prodotto (Fase 11C — 2026-05-15)
      const cfg = d.configurazione;
      const eta = calcEta();
      if (!cfg.prodotto_tipo) return "Seleziona un prodotto: RSM o Pacchetto Target";
      if (cfg.prodotto_tipo === "rsm") {
        if (!cfg.durata_rsm) return "Seleziona la durata del contratto RSM";
        if (cfg.durata_rsm === "vita_intera" && eta !== null && eta > 60) {
          return "La Formula Vita Intera è disponibile solo fino a 60 anni d'età (Tariffa RiS pag. 10). Scegli Annuale o Poliennale.";
        }
        if (["annuale", "poliennale"].includes(cfg.durata_rsm) && eta !== null && eta >= 75) {
          return "Durata Definita ha limite di permanenza a 75 anni (Tariffa RiS pag. 11).";
        }
      } else if (cfg.prodotto_tipo === "target") {
        if (cfg.target_sezioni.length === 0) return "Seleziona almeno una sezione Target (Kids / Dental / Fisio)";
        if (eta !== null && eta > 74) return "Formula Target ha limite di ingresso 74 anni (Tariffa RiS pag. 11).";
        if (cfg.target_sezioni.includes("kids") && eta !== null && eta > 18) {
          return "Sezione Kids: limite di permanenza 18 anni (Tariffa RiS pag. 11). Deseleziona Kids o cambia assicurato.";
        }
      }
    }
    if (step === 6) {
      if (!d.consensi.privacy) return "Conferma la lettura dell'informativa privacy";
      if (!d.consensi.dati_sanitari_informativa) return "Conferma la lettura dell'informativa rinforzata art. 9 GDPR";
      if (!d.questionario_sanitario.consenso_art9_gdpr) return "Acconsenti al trattamento dei dati sanitari (art. 9 GDPR)";
    }
    return null;
  };

  const next = () => {
    const err = stepValid();
    if (err) { toast.error(err); return; }
    if (step < STEPS.length - 1) setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const prev = () => { if (step > 0) setStep((s) => s - 1); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const submit = async () => {
    const err = stepValid();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...d,
        anagrafica: { ...d.anagrafica, codice_fiscale: d.anagrafica.codice_fiscale.toUpperCase() },
        // FASE K2 iter 69 — Opt-in account cliente + partner tracking (convenzione)
        crea_account_ok: !!d.crea_account_ok,
        partner: partnerId,
        sede: sede,
        azienda: azienda,
        // v1.56 — Click-wrap IVASS
        signed_declaration: !!signedDeclaration,
        session_hash: sessionHashRIS,
        mup_visione: buildMupVisioneMeta({ mupSeen, mupFilename }),
        consensi_granulari: buildConsensiGranulariMeta(consensi),
        // v1.72.3 — Preferenze ricontatto (canale + fascia oraria)
        preferenze_ricontatto: (preferenzeRicontatto.canale || preferenzeRicontatto.fascia)
          ? preferenzeRicontatto : null,
      };
      const { data } = await axios.post(`${API}/rischi-salute/richiesta`, payload);
      setSuccess(data);
      try {
        trackWizardSubmit({ ramo: "salute" });
        if (data?.account_cliente?.created) trackAccountCreated({ ramo: "salute" });
      } catch { /* ignore */ }
      try { localStorage.removeItem(STORAGE_KEY); } catch {/* */}
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg;
      if (Array.isArray(detail)) msg = detail.map((x) => x.msg).join(" — ");
      else if (typeof detail === "object" && detail !== null) msg = detail.message || "Errore invio richiesta";
      else msg = detail || "Errore invio richiesta";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ====== SUCCESS ======
  if (success) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="ris-success">
          <div className="max-w-xl mx-auto px-5 py-16 text-center">
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-9 h-9 text-emerald-600" />
            </div>
            <h1 className="font-heading text-3xl font-medium text-[#0F1F14] mb-3">Richiesta inviata!</h1>
            <p className="text-sm text-[#4A5E51] leading-relaxed mb-6">{success.message}</p>
            <div className="bg-white border border-[#E5E2DB] rounded-2xl p-5 text-left mb-6">
              <p className="text-xs text-[#4A5E51] mb-1">Codice richiesta</p>
              <p className="font-mono text-sm text-[#0F1F14] break-all">{success.request_id}</p>
            </div>
            {/* FASE K2 iter 69 — Credenziali one-time se opt-in account cliente */}
            {success.account_cliente?.created && (
              <div className="text-left mb-6">
                <AccountCredentialsBox account={success.account_cliente} />
              </div>
            )}
            <Link to="/" className="inline-flex items-center gap-2 px-6 h-11 rounded-full bg-[#163321] text-white text-sm font-semibold hover:bg-[#1d4a2e] transition-all" data-testid="ris-back-home-btn">
              Torna alla Home
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="ris-page">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 py-8 sm:py-12">
          <Link to="/famiglia" className="text-sm text-[#4A5E51] hover:text-[#163321] flex items-center gap-1 mb-6" data-testid="ris-back-link">
            <ArrowLeft className="w-4 h-4" /> Torna alle Polizze Famiglia
          </Link>

          <div className="flex items-start gap-3 mb-6 sm:mb-8">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-[#163321]/10 flex items-center justify-center shrink-0">
              <Heart className="w-6 h-6 sm:w-7 sm:h-7 text-[#163321]" />
            </div>
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#0F1F14] leading-tight">Quotatore Polizza Sanitaria</h1>
              <p className="text-sm text-[#4A5E51] mt-1"><strong>RealmenteInSalute</strong> · Reale Mutua · distribuita da Colucci Assicuratori</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-6 flex items-start gap-2 text-[12px] text-blue-900">
            <Shield className="w-4 h-4 shrink-0 mt-0.5" />
            <p>I dati sanitari raccolti sono <strong>cifrati con AES-256</strong> al momento del salvataggio. Accesso riservato al consulente assicurativo e al <Link to="/privacy/dpo" className="underline">DPO</Link>. Riceverai un preventivo personalizzato dal consulente entro 24h lavorative.</p>
          </div>

          <StepProgress steps={STEPS} currentStep={step} onStepClick={(i) => i < step && setStep(i)} progress={Math.round(((step + 1) / STEPS.length) * 100)} testIdPrefix="ris" />

          <Card data-testid={`ris-step-${step}`}>
            {/* ============ STEP 0 — Anagrafica ============ */}
            {step === 0 && (
              <div className="space-y-4">
                <h2 className="font-heading text-lg text-[#0F1F14] font-medium">Anagrafica del contraente</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Nome *</Label>
                    <Input value={d.anagrafica.nome} onChange={(e) => upd("anagrafica.nome", e.target.value)} data-testid="ris-nome" />
                  </div>
                  <div>
                    <Label>Cognome *</Label>
                    <Input value={d.anagrafica.cognome} onChange={(e) => upd("anagrafica.cognome", e.target.value)} data-testid="ris-cognome" />
                  </div>
                  <div>
                    <Label>Sesso *</Label>
                    <Select value={d.anagrafica.sesso} onValueChange={(v) => upd("anagrafica.sesso", v)}>
                      <SelectTrigger data-testid="ris-sesso"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="M">Maschio</SelectItem>
                        <SelectItem value="F">Femmina</SelectItem>
                        <SelectItem value="altro">Altro / Preferisco non specificare</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Data di nascita *</Label>
                    <Input type="date" value={d.anagrafica.data_nascita} onChange={(e) => upd("anagrafica.data_nascita", e.target.value)} data-testid="ris-data-nascita" />
                    {(() => {
                      if (!d.anagrafica.data_nascita) return null;
                      const eta = Math.floor((Date.now() - new Date(d.anagrafica.data_nascita)) / (365.25 * 24 * 3600 * 1000));
                      if (eta >= 75) {
                        return (
                          <p className="text-[11px] text-rose-700 mt-1 flex items-center gap-1" data-testid="ris-eta-blocco">
                            <AlertTriangle className="w-3 h-3" /> Età {eta}: oltre il limite di permanenza in assicurazione (75 anni, Tariffa RiS pag. 11).
                          </p>
                        );
                      }
                      if (eta > 60) {
                        return (
                          <p className="text-[11px] text-amber-700 mt-1 flex items-center gap-1" data-testid="ris-eta-warning">
                            <Info className="w-3 h-3" /> Età {eta}: la formula Vita Intera non è disponibile (limite ingresso 60 anni, Tariffa pag. 10). Procedi con Durata Definita.
                          </p>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  <div>
                    <Label>Luogo di nascita *</Label>
                    <ComuneAutocomplete
                      value={d.anagrafica.luogo_nascita}
                      onChange={(v) => upd("anagrafica.luogo_nascita", v)}
                      placeholder="Es. Napoli"
                      data-testid="ris-luogo-nascita"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Codice fiscale *</Label>
                    <CodiceFiscaleField
                      value={d.anagrafica.codice_fiscale}
                      onChange={(v) => upd("anagrafica.codice_fiscale", v)}
                      cognome={d.anagrafica.cognome}
                      nome={d.anagrafica.nome}
                      dataNascita={d.anagrafica.data_nascita}
                      sesso={d.anagrafica.sesso}
                      luogoNascita={d.anagrafica.luogo_nascita}
                    />
                  </div>
                  <div>
                    <Label>Email *<PrivacyFieldTooltip finalita="email" testIdPrefix="ris-email" /></Label>
                    <Input type="email" value={d.anagrafica.email} onChange={(e) => upd("anagrafica.email", e.target.value)} data-testid="ris-email" />
                  </div>
                  <div>
                    <Label>Telefono *<PrivacyFieldTooltip finalita="telefono" testIdPrefix="ris-telefono" /></Label>
                    <Input type="tel" value={d.anagrafica.telefono} onChange={(e) => upd("anagrafica.telefono", e.target.value)} placeholder="+39 333 1234567" data-testid="ris-tel" />
                  </div>
                  <div>
                    <Label>Città *</Label>
                    <Input value={d.anagrafica.citta} onChange={(e) => upd("anagrafica.citta", e.target.value)} data-testid="ris-citta" />
                  </div>
                  <div>
                    <Label>Provincia *</Label>
                    <Select value={d.anagrafica.provincia} onValueChange={(v) => upd("anagrafica.provincia", v)}>
                      <SelectTrigger data-testid="ris-provincia"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent className="max-h-60">{PROVINCE.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* ============ STEP 1 — Nucleo ============ */}
            {step === 1 && (
              <div className="space-y-4">
                <h2 className="font-heading text-lg text-[#0F1F14] font-medium">Nucleo familiare da assicurare</h2>
                <p className="text-xs text-[#4A5E51]">Indica chi vuoi includere nella polizza. La quotazione varia in base ai componenti.</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  {[{ id: "singolo", label: "Singolo", desc: "Solo te" }, { id: "coppia", label: "Coppia", desc: "Tu + coniuge / convivente" }, { id: "famiglia", label: "Famiglia", desc: "Tu + coniuge + figli" }].map((n) => (
                    <OptionCard key={n.id} active={d.nucleo.composizione === n.id} onClick={() => upd("nucleo.composizione", n.id)} title={n.label} desc={n.desc} icon={UsersIcon} testid={`ris-nucleo-${n.id}`} />
                  ))}
                </div>

                {(d.nucleo.composizione === "coppia" || d.nucleo.composizione === "famiglia") && (
                  <div className="flex items-center gap-2 mt-2">
                    <Checkbox id="coniuge" checked={d.nucleo.coniuge_da_assicurare} onCheckedChange={(v) => upd("nucleo.coniuge_da_assicurare", !!v)} data-testid="ris-coniuge" />
                    <Label htmlFor="coniuge" className="text-sm cursor-pointer">Includere il coniuge / convivente nella polizza</Label>
                  </div>
                )}

                {d.nucleo.composizione === "famiglia" && (
                  <div className="mt-3">
                    <Label className="mb-2 block">Età approssimative dei figli da assicurare</Label>
                    <div className="space-y-2">
                      {d.nucleo.figli.map((f, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-xs text-[#4A5E51] w-14">Figlio {i + 1}</span>
                          <Input type="number" min={0} max={30} value={f.eta} onChange={(e) => updFiglio(i, e.target.value)} className="max-w-[120px]" data-testid={`ris-figlio-${i}`} />
                          <span className="text-xs text-[#4A5E51]">anni</span>
                          <button type="button" onClick={() => rmFiglio(i)} className="ml-auto text-xs text-red-600 hover:underline">Rimuovi</button>
                        </div>
                      ))}
                      {d.nucleo.figli.length < 10 && (
                        <Button type="button" variant="outline" size="sm" onClick={addFiglio} data-testid="ris-add-figlio">+ Aggiungi figlio</Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ============ STEP 2 — Questionario sanitario ============ */}
            {step === 2 && (
              <div className="space-y-5">
                <div>
                  <h2 className="font-heading text-lg text-[#0F1F14] font-medium">Questionario sanitario</h2>
                  <p className="text-xs text-[#4A5E51] mt-1">Rispondi alle domande del consulente. Ai sensi del principio di minimizzazione (art. 5 GDPR) raccogliamo <strong>solo le informazioni strettamente necessarie</strong> a impostare il preventivo.</p>
                </div>

                {/* Domanda iniziale + flag generali */}
                <div className="space-y-3">
                  <div className="bg-[#F5F4F0] border border-[#E5E2DB] rounded-xl p-3">
                    <Label className="text-sm font-medium text-[#0F1F14] block mb-2">Hai attualmente patologie diagnosticate o condizioni di salute particolari? *</Label>
                    <RadioGroup
                      value={d.questionario_sanitario.ha_patologie_dichiarate === null ? "" : String(d.questionario_sanitario.ha_patologie_dichiarate)}
                      onValueChange={(v) => upd("questionario_sanitario.ha_patologie_dichiarate", v === "true")}
                      className="flex gap-3"
                    >
                      <div className="flex items-center gap-1.5">
                        <RadioGroupItem id="haspat-no" value="false" data-testid="ris-haspat-no" />
                        <Label htmlFor="haspat-no" className="text-sm cursor-pointer">No</Label>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <RadioGroupItem id="haspat-si" value="true" data-testid="ris-haspat-si" />
                        <Label htmlFor="haspat-si" className="text-sm cursor-pointer">Sì</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="flex items-center justify-between bg-[#F5F4F0] rounded-xl p-3 gap-3">
                    <Label htmlFor="fumatore" className="text-sm cursor-pointer flex-1">Sei fumatore? (sigarette, sigari, e-cig)</Label>
                    <RadioGroup value={String(d.questionario_sanitario.fumatore)} onValueChange={(v) => upd("questionario_sanitario.fumatore", v === "true")} className="flex gap-3">
                      <div className="flex items-center gap-1.5"><RadioGroupItem id="fum-no" value="false" data-testid="ris-fumatore-no" /><Label htmlFor="fum-no" className="text-xs cursor-pointer">No</Label></div>
                      <div className="flex items-center gap-1.5"><RadioGroupItem id="fum-si" value="true" data-testid="ris-fumatore-si" /><Label htmlFor="fum-si" className="text-xs cursor-pointer">Sì</Label></div>
                    </RadioGroup>
                  </div>

                  <div className="flex items-center justify-between bg-[#F5F4F0] rounded-xl p-3 gap-3">
                    <Label htmlFor="sport" className="text-sm cursor-pointer flex-1">Pratichi sport agonistici o attività ad alto rischio?</Label>
                    <RadioGroup value={String(d.questionario_sanitario.sport_agonistici_o_alto_rischio)} onValueChange={(v) => upd("questionario_sanitario.sport_agonistici_o_alto_rischio", v === "true")} className="flex gap-3">
                      <div className="flex items-center gap-1.5"><RadioGroupItem id="spo-no" value="false" data-testid="ris-sport-no" /><Label htmlFor="spo-no" className="text-xs cursor-pointer">No</Label></div>
                      <div className="flex items-center gap-1.5"><RadioGroupItem id="spo-si" value="true" data-testid="ris-sport-si" /><Label htmlFor="spo-si" className="text-xs cursor-pointer">Sì</Label></div>
                    </RadioGroup>
                  </div>
                </div>

                {/* Altezza/Peso/BMI live */}
                <div className="bg-white border border-[#E5E2DB] rounded-xl p-3 space-y-3" data-testid="ris-bmi-section">
                  <Label className="text-sm font-medium text-[#0F1F14]">Altezza e peso (per BMI indicativo)</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-[#4A5E51]">Altezza</span>
                        <span className="text-sm font-mono font-semibold">{d.stile_vita.altezza_cm} cm</span>
                      </div>
                      <input type="range" min="120" max="220" value={d.stile_vita.altezza_cm} onChange={(e) => upd("stile_vita.altezza_cm", parseInt(e.target.value, 10))} className="w-full accent-[#163321]" data-testid="ris-altezza" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-[#4A5E51]">Peso</span>
                        <span className="text-sm font-mono font-semibold">{d.stile_vita.peso_kg} kg</span>
                      </div>
                      <input type="range" min="30" max="200" step="0.5" value={d.stile_vita.peso_kg} onChange={(e) => upd("stile_vita.peso_kg", parseFloat(e.target.value))} className="w-full accent-[#163321]" data-testid="ris-peso" />
                    </div>
                  </div>
                  {(() => {
                    const bmi = calcBMI(d.stile_vita.altezza_cm, d.stile_vita.peso_kg);
                    if (!bmi) return null;
                    const colorMap = {
                      "Sottopeso": "bg-blue-100 text-blue-800",
                      "Normopeso": "bg-emerald-100 text-emerald-800",
                      "Sovrappeso": "bg-amber-100 text-amber-800",
                      "Obesità classe I": "bg-orange-100 text-orange-800",
                      "Obesità classe II": "bg-red-100 text-red-800",
                      "Obesità classe III": "bg-red-200 text-red-900",
                    };
                    return (
                      <div className="flex items-center gap-2 text-sm" data-testid="ris-bmi-display">
                        <span className="text-[#4A5E51]">BMI:</span>
                        <span className="font-mono font-semibold text-[#0F1F14]">{bmi.bmi}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${colorMap[bmi.label]}`}>{bmi.label}</span>
                      </div>
                    );
                  })()}
                </div>

                {/* Domande sempre visibili: 5 nuove specifiche del PDF Reale Mutua */}
                <div className="space-y-2 pt-2">
                  <p className="text-[11px] uppercase tracking-wide font-semibold text-[#4A5E51]">Domande del questionario sanitario assicurativo</p>
                  {[
                    { key: "colesterolo_oltre_250", label: "Colesterolo > 250 mg/dl?", testid: "colesterolo" },
                    { key: "pressione_arteriosa_elevata", label: "Hai ipertensione arteriosa diagnosticata?", testid: "pressione" },
                    { key: "sostanze_stupefacenti_alcol_problematico", label: "Hai mai fatto uso di sostanze stupefacenti o alcol in modo problematico? (domanda obbligatoria del questionario)", testid: "stupefacenti" },
                    { key: "esami_fuori_norma_in_attesa", label: "Hai esami clinici recenti fuori norma o accertamenti in attesa?", testid: "esami" },
                  ].map((q) => (
                    <div key={q.key} className="flex items-center justify-between bg-[#F5F4F0] rounded-xl p-3 gap-3">
                      <Label className="text-sm cursor-pointer flex-1">{q.label}</Label>
                      <RadioGroup value={String(d.questionario_sanitario[q.key])} onValueChange={(v) => upd(`questionario_sanitario.${q.key}`, v === "true")} className="flex gap-3">
                        <div className="flex items-center gap-1.5"><RadioGroupItem id={`${q.testid}-no`} value="false" data-testid={`ris-${q.testid}-no`} /><Label htmlFor={`${q.testid}-no`} className="text-xs cursor-pointer">No</Label></div>
                        <div className="flex items-center gap-1.5"><RadioGroupItem id={`${q.testid}-si`} value="true" data-testid={`ris-${q.testid}-si`} /><Label htmlFor={`${q.testid}-si`} className="text-xs cursor-pointer">Sì</Label></div>
                      </RadioGroup>
                    </div>
                  ))}

                  {/* Invalidità con percentuale */}
                  <div className="bg-[#F5F4F0] rounded-xl p-3">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <Label className="text-sm cursor-pointer flex-1">Hai invalidità civile riconosciuta?</Label>
                      <RadioGroup value={String(d.questionario_sanitario.invalidita_civile)} onValueChange={(v) => upd("questionario_sanitario.invalidita_civile", v === "true")} className="flex gap-3">
                        <div className="flex items-center gap-1.5"><RadioGroupItem id="inv-no" value="false" data-testid="ris-invalidita-no" /><Label htmlFor="inv-no" className="text-xs cursor-pointer">No</Label></div>
                        <div className="flex items-center gap-1.5"><RadioGroupItem id="inv-si" value="true" data-testid="ris-invalidita-si" /><Label htmlFor="inv-si" className="text-xs cursor-pointer">Sì</Label></div>
                      </RadioGroup>
                    </div>
                    {d.questionario_sanitario.invalidita_civile && (
                      <div className="mt-2 flex items-center gap-2">
                        <Label className="text-xs text-[#4A5E51]">Percentuale:</Label>
                        <Input type="number" min={0} max={100} value={d.questionario_sanitario.invalidita_percentuale ?? ""} onChange={(e) => upd("questionario_sanitario.invalidita_percentuale", e.target.value === "" ? null : parseInt(e.target.value, 10))} className="max-w-[100px]" data-testid="ris-invalidita-pct" />
                        <span className="text-sm text-[#0F1F14]">%</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sezione condizionale */}
                {d.questionario_sanitario.ha_patologie_dichiarate === true && (
                  <div className="space-y-4 border-l-4 border-[#D97B58] pl-4 py-2" data-testid="ris-sezione-condizionale">
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-[12px] text-blue-900">
                      <p><strong>Diritto all'oblio oncologico (L. 193/2023).</strong> Se hai avuto patologie oncologiche <strong>risolte da oltre 10 anni</strong> (5 anni se sotto i 21 anni di età), puoi <strong>NON dichiararle</strong>. Questo è un tuo diritto.</p>
                    </div>

                    {/* Ricoveri */}
                    <div className="bg-white border border-[#E5E2DB] rounded-xl p-3">
                      <Label className="text-sm font-medium text-[#0F1F14] block mb-2">Hai mai avuto ricoveri ospedalieri negli ultimi 5 anni? *</Label>
                      <Select value={d.questionario_sanitario.ricoveri_ultimi_5_anni || ""} onValueChange={(v) => upd("questionario_sanitario.ricoveri_ultimi_5_anni", v)}>
                        <SelectTrigger data-testid="ris-ricoveri"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="nessuno">Nessuno</SelectItem>
                          <SelectItem value="1">1 ricovero</SelectItem>
                          <SelectItem value="2-3">2 o 3 ricoveri</SelectItem>
                          <SelectItem value="oltre_3">Oltre 3 ricoveri</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Terapie */}
                    <div className="bg-white border border-[#E5E2DB] rounded-xl p-3">
                      <Label className="text-sm font-medium text-[#0F1F14] block mb-2">Stai seguendo terapie continuative (farmacologiche, riabilitative)? *</Label>
                      <RadioGroup
                        value={d.questionario_sanitario.terapie_continuative === null ? "" : String(d.questionario_sanitario.terapie_continuative)}
                        onValueChange={(v) => upd("questionario_sanitario.terapie_continuative", v === "true")}
                        className="flex gap-3 mb-2"
                      >
                        <div className="flex items-center gap-1.5"><RadioGroupItem id="ter-no" value="false" data-testid="ris-terapie-no" /><Label htmlFor="ter-no" className="text-xs cursor-pointer">No</Label></div>
                        <div className="flex items-center gap-1.5"><RadioGroupItem id="ter-si" value="true" data-testid="ris-terapie-si" /><Label htmlFor="ter-si" className="text-xs cursor-pointer">Sì</Label></div>
                      </RadioGroup>
                      {d.questionario_sanitario.terapie_continuative === true && (
                        <div className="mt-2 grid sm:grid-cols-2 gap-1.5">
                          {CATEGORIE_TERAPIE.map((c) => {
                            const checked = d.questionario_sanitario.terapie_categorie.includes(c.id);
                            return (
                              <label key={c.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-xs ${checked ? "border-[#163321] bg-[#163321]/5" : "border-[#E5E2DB] bg-white"}`} data-testid={`ris-cat-terapia-${c.id}`}>
                                <Checkbox checked={checked} onCheckedChange={() => toggleTerapiaCategoria(c.id)} />
                                <span>{c.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Interventi */}
                    <div className="bg-white border border-[#E5E2DB] rounded-xl p-3">
                      <Label className="text-sm font-medium text-[#0F1F14] block mb-2">Hai mai avuto interventi chirurgici importanti? *</Label>
                      <RadioGroup
                        value={d.questionario_sanitario.interventi_chirurgici_importanti === null ? "" : String(d.questionario_sanitario.interventi_chirurgici_importanti)}
                        onValueChange={(v) => upd("questionario_sanitario.interventi_chirurgici_importanti", v === "true")}
                        className="flex gap-3"
                      >
                        <div className="flex items-center gap-1.5"><RadioGroupItem id="int-no" value="false" data-testid="ris-interventi-no" /><Label htmlFor="int-no" className="text-xs cursor-pointer">No</Label></div>
                        <div className="flex items-center gap-1.5"><RadioGroupItem id="int-si" value="true" data-testid="ris-interventi-si" /><Label htmlFor="int-si" className="text-xs cursor-pointer">Sì</Label></div>
                      </RadioGroup>
                    </div>

                    {/* Patologia cronica */}
                    <div className="bg-white border border-[#E5E2DB] rounded-xl p-3">
                      <Label className="text-sm font-medium text-[#0F1F14] block mb-2">Hai una patologia cronica già diagnosticata? *</Label>
                      <RadioGroup
                        value={d.questionario_sanitario.patologia_cronica === null ? "" : String(d.questionario_sanitario.patologia_cronica)}
                        onValueChange={(v) => upd("questionario_sanitario.patologia_cronica", v === "true")}
                        className="flex gap-3 mb-2"
                      >
                        <div className="flex items-center gap-1.5"><RadioGroupItem id="cro-no" value="false" data-testid="ris-cronica-no" /><Label htmlFor="cro-no" className="text-xs cursor-pointer">No</Label></div>
                        <div className="flex items-center gap-1.5"><RadioGroupItem id="cro-si" value="true" data-testid="ris-cronica-si" /><Label htmlFor="cro-si" className="text-xs cursor-pointer">Sì</Label></div>
                      </RadioGroup>
                      {d.questionario_sanitario.patologia_cronica === true && (
                        <div className="mt-2 grid sm:grid-cols-2 gap-1.5">
                          {CATEGORIE_TERAPIE.map((c) => {
                            const checked = d.questionario_sanitario.patologia_cronica_categoria.includes(c.id);
                            return (
                              <label key={c.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-xs ${checked ? "border-[#163321] bg-[#163321]/5" : "border-[#E5E2DB] bg-white"}`} data-testid={`ris-cat-cronica-${c.id}`}>
                                <Checkbox checked={checked} onCheckedChange={() => togglePatologiaCronicaCategoria(c.id)} />
                                <span>{c.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Dichiarazione veridicità */}
                    <label className="flex items-start gap-2.5 cursor-pointer bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="ris-dichiarazione-veridicita">
                      <Checkbox checked={d.questionario_sanitario.dichiarazione_veridicita} onCheckedChange={(v) => upd("questionario_sanitario.dichiarazione_veridicita", !!v)} className="mt-0.5" />
                      <span className="text-[12px] text-amber-900 leading-relaxed">Dichiaro che le informazioni fornite sono <strong>veritiere e complete</strong>. Sono consapevole che dichiarazioni inesatte o reticenze possono comportare la decadenza della copertura ai sensi degli <strong>artt. 1892-1893 c.c.</strong> *</span>
                    </label>
                  </div>
                )}

                {/* Dichiarazione "no patologie" */}
                {d.questionario_sanitario.ha_patologie_dichiarate === false && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-[12px] text-emerald-900">
                    <p>Dichiaro di <strong>non avere patologie o condizioni di salute rilevanti</strong> ai fini della valutazione del rischio. Il consulente effettuerà comunque il questionario sanitario contrattuale completo in fase di emissione.</p>
                  </div>
                )}

                {/* Disclaimer GDPR rinforzato */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-[11px] text-blue-900 leading-relaxed">
                  <p className="font-semibold mb-1.5 flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> Tutela dei dati sanitari (art. 9 GDPR)</p>
                  <p>I dati sanitari forniti vengono trattati ai sensi dell'<strong>art. 9 c.2 lett. a (consenso esplicito)</strong> e <strong>g (valutazione del rischio assicurativo)</strong> GDPR. Sono <strong>cifrati con AES-256</strong> al momento del salvataggio. L'accesso è riservato esclusivamente al consulente assicurativo incaricato e al DPO designato. Conservazione: <strong>5 anni</strong> dalla chiusura della richiesta. Diritti art. 15-22 GDPR esercitabili contattando il <Link to="/privacy/dpo" target="_blank" className="underline">DPO</Link>.</p>
                </div>
              </div>
            )}

            {/* ============ STEP 3 — Professione (NUOVO) ============ */}
            {step === 3 && (
              <div className="space-y-4" data-testid="ris-step-professione">
                <h2 className="font-heading text-lg text-[#0F1F14] font-medium">Attività professionale e sportiva</h2>
                <p className="text-xs text-[#4A5E51]">Domande del questionario sanitario assicurativo (Q13-Q14 Mod. 5076 Reale Mutua).</p>

                <div>
                  <Label>Professione *</Label>
                  <Input list="prof-list" value={d.professione.professione} onChange={(e) => upd("professione.professione", e.target.value)} maxLength={120} placeholder="Es. Impiegato, Medico, Imprenditore..." data-testid="ris-professione" />
                  <datalist id="prof-list">{PROFESSIONI_COMUNI.map((p) => <option key={p} value={p} />)}</datalist>
                </div>

                <div>
                  <Label>Settore di attività</Label>
                  <Input value={d.professione.settore} onChange={(e) => upd("professione.settore", e.target.value)} maxLength={120} placeholder="Es. Banche, Sanità, Manifatturiero..." data-testid="ris-settore" />
                </div>

                <div className="bg-[#F5F4F0] rounded-xl p-3">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <Label className="text-sm cursor-pointer flex-1">Lavori stabilmente all'estero?</Label>
                    <RadioGroup value={String(d.professione.lavora_estero)} onValueChange={(v) => upd("professione.lavora_estero", v === "true")} className="flex gap-3">
                      <div className="flex items-center gap-1.5"><RadioGroupItem id="lav-no" value="false" data-testid="ris-lavora-estero-no" /><Label htmlFor="lav-no" className="text-xs cursor-pointer">No</Label></div>
                      <div className="flex items-center gap-1.5"><RadioGroupItem id="lav-si" value="true" data-testid="ris-lavora-estero-si" /><Label htmlFor="lav-si" className="text-xs cursor-pointer">Sì</Label></div>
                    </RadioGroup>
                  </div>
                  {d.professione.lavora_estero && (
                    <div className="mt-2">
                      <Label className="text-xs">Paese estero *</Label>
                      <Input value={d.professione.paese_estero} onChange={(e) => upd("professione.paese_estero", e.target.value)} placeholder="Es. Germania, Svizzera..." data-testid="ris-paese-estero" />
                    </div>
                  )}
                </div>

                <div className="bg-[#F5F4F0] rounded-xl p-3">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <Label className="text-sm cursor-pointer flex-1">Pratichi sport?</Label>
                    <RadioGroup value={String(d.professione.pratica_sport)} onValueChange={(v) => upd("professione.pratica_sport", v === "true")} className="flex gap-3">
                      <div className="flex items-center gap-1.5"><RadioGroupItem id="sp-no" value="false" data-testid="ris-pratica-sport-no" /><Label htmlFor="sp-no" className="text-xs cursor-pointer">No</Label></div>
                      <div className="flex items-center gap-1.5"><RadioGroupItem id="sp-si" value="true" data-testid="ris-pratica-sport-si" /><Label htmlFor="sp-si" className="text-xs cursor-pointer">Sì</Label></div>
                    </RadioGroup>
                  </div>
                  {d.professione.pratica_sport && (
                    <div className="space-y-2 mt-2">
                      <div>
                        <Label className="text-xs">Quale sport? *</Label>
                        <Input list="sport-list" value={d.professione.sport_dettaglio} onChange={(e) => upd("professione.sport_dettaglio", e.target.value)} placeholder="Es. Calcio, Tennis, Sci..." data-testid="ris-sport-dettaglio" />
                        <datalist id="sport-list">{SPORT_COMUNI.map((s) => <option key={s} value={s} />)}</datalist>
                      </div>
                      <div className="flex items-center justify-between gap-3 bg-white rounded-lg p-2 border border-[#E5E2DB]">
                        <Label className="text-xs cursor-pointer flex-1">Lo sport è praticato a livello professionistico/agonistico federato?</Label>
                        <RadioGroup value={String(d.professione.sport_professionistico)} onValueChange={(v) => upd("professione.sport_professionistico", v === "true")} className="flex gap-3">
                          <div className="flex items-center gap-1.5"><RadioGroupItem id="spro-no" value="false" data-testid="ris-sport-pro-no" /><Label htmlFor="spro-no" className="text-xs cursor-pointer">No</Label></div>
                          <div className="flex items-center gap-1.5"><RadioGroupItem id="spro-si" value="true" data-testid="ris-sport-pro-si" /><Label htmlFor="spro-si" className="text-xs cursor-pointer">Sì</Label></div>
                        </RadioGroup>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ============ STEP 4 — Prodotto (Fase 11C) ============ */}
            {step === 4 && (() => {
              const eta = calcEta();
              const cfg = d.configurazione;
              const vitaInteraBlocked = eta !== null && eta > 60;
              const ddBlocked = eta !== null && eta >= 75;
              const targetBlocked = eta !== null && eta > 74;
              const kidsBlocked = eta !== null && eta > 18;
              return (
                <div className="space-y-5" data-testid="ris-step-prodotto">
                  {/* Step 4a — Asse Prodotto */}
                  <div>
                    <h2 className="font-heading text-lg text-[#0F1F14] font-medium">Quale prodotto vuoi attivare?</h2>
                    <p className="text-xs text-[#4A5E51] mt-1 mb-3">
                      Scegli tra <strong>RSM</strong> (copertura ricovero + extra ricovero) e <strong>Target</strong> (pacchetti specialistici Kids/Dental/Fisio, anche combinabili).
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <OptionCard
                        active={cfg.prodotto_tipo === "rsm"}
                        onClick={() => setProdottoTipo("rsm")}
                        title="Rimborso Spese Mediche (RSM)"
                        desc="Copertura completa per ricoveri, interventi e visite specialistiche. Opzioni Comfort (massimale €300k) / Deluxe (€500k)."
                        icon={Shield}
                        badge="Consigliato"
                        testid="ris-prod-tipo-rsm"
                      />
                      <OptionCard
                        active={cfg.prodotto_tipo === "target"}
                        onClick={() => setProdottoTipo("target")}
                        title="Pacchetto Target"
                        desc="Pacchetti specialistici a sé stanti: Kids, Dental, Fisio. Durata annuale. Acquistabili anche separatamente."
                        icon={Sparkles}
                        testid="ris-prod-tipo-target"
                      />
                    </div>
                  </div>

                  {/* Step 4b — RSM → scelta durata */}
                  {cfg.prodotto_tipo === "rsm" && (
                    <div data-testid="ris-step-durata-rsm">
                      <h3 className="font-medium text-sm text-[#0F1F14] mb-1">Scegli la durata del contratto</h3>
                      <p className="text-xs text-[#4A5E51] mb-3">
                        Tre Formule contrattuali ufficiali Reale Mutua (Mod. 5169 Durata Definita / Mod. 5170 Vita Intera).
                      </p>
                      <div className="grid sm:grid-cols-3 gap-3">
                        <button
                          type="button"
                          onClick={() => !ddBlocked && setDurataRsm("annuale")}
                          disabled={ddBlocked}
                          data-testid="ris-durata-annuale"
                          className={`text-left rounded-2xl border-2 p-4 transition-all w-full ${cfg.durata_rsm === "annuale" ? "border-[#163321] bg-[#163321]/5 shadow-lg" : "border-[#E5E2DB] bg-white hover:border-[#163321]/40"} ${ddBlocked ? "opacity-50 cursor-not-allowed" : ""}`}
                          title={ddBlocked ? "Limite permanenza 75 anni" : ""}
                        >
                          <p className="font-medium text-[#0F1F14]">Annuale</p>
                          <p className="text-[11px] text-[#4A5E51] mt-1">Durata 1 anno, tacito rinnovo. Mod. 5169.</p>
                          <p className="text-[10px] text-amber-700 mt-1">⚠️ Parto escluso (PDF pag. 11)</p>
                        </button>
                        <button
                          type="button"
                          onClick={() => !ddBlocked && setDurataRsm("poliennale")}
                          disabled={ddBlocked}
                          data-testid="ris-durata-poliennale"
                          className={`text-left rounded-2xl border-2 p-4 transition-all w-full ${cfg.durata_rsm === "poliennale" ? "border-[#163321] bg-[#163321]/5 shadow-lg" : "border-[#E5E2DB] bg-white hover:border-[#163321]/40"} ${ddBlocked ? "opacity-50 cursor-not-allowed" : ""}`}
                          title={ddBlocked ? "Limite permanenza 75 anni" : ""}
                        >
                          <p className="font-medium text-[#0F1F14]">Poliennale</p>
                          <p className="text-[11px] text-[#4A5E51] mt-1">Durata pluriennale. Mod. 5169. Premio bloccato fino a scadenza.</p>
                          <p className="text-[10px] text-emerald-700 mt-1">✓ Parto incluso</p>
                        </button>
                        <button
                          type="button"
                          onClick={() => !vitaInteraBlocked && setDurataRsm("vita_intera")}
                          disabled={vitaInteraBlocked}
                          data-testid="ris-durata-vita-intera"
                          className={`text-left rounded-2xl border-2 p-4 transition-all w-full ${cfg.durata_rsm === "vita_intera" ? "border-[#163321] bg-[#163321]/5 shadow-lg" : "border-[#E5E2DB] bg-white hover:border-[#163321]/40"} ${vitaInteraBlocked ? "opacity-50 cursor-not-allowed" : ""}`}
                          title={vitaInteraBlocked ? "Disponibile solo fino a 60 anni d'età (PDF pag. 10)" : ""}
                        >
                          <p className="font-medium text-[#0F1F14]">Vita Intera</p>
                          <p className="text-[11px] text-[#4A5E51] mt-1">Copertura a vita con rinnovo annuale. Mod. 5170.</p>
                          {vitaInteraBlocked && (
                            <p className="text-[10px] text-rose-700 mt-1" data-testid="ris-vi-blocked-msg">
                              Disponibile fino a 60 anni
                            </p>
                          )}
                        </button>
                      </div>
                      {ddBlocked && (
                        <p className="text-[11px] text-rose-700 mt-2 flex items-center gap-1" data-testid="ris-dd-blocked-msg">
                          <AlertTriangle className="w-3 h-3" /> Età ≥ 75: limite di permanenza in assicurazione (PDF pag. 11).
                        </p>
                      )}
                    </div>
                  )}

                  {/* Step 4b — Target → scelta sezioni */}
                  {cfg.prodotto_tipo === "target" && (
                    <div data-testid="ris-step-sezioni-target">
                      <h3 className="font-medium text-sm text-[#0F1F14] mb-1">Quali pacchetti vuoi attivare?</h3>
                      <p className="text-xs text-[#4A5E51] mb-3">
                        Formula Target (Mod. 5168). Durata annuale. Puoi selezionare una, due o tutte e tre le sezioni.
                      </p>
                      <div className="space-y-2">
                        {[
                          { id: "kids", label: "Pacchetto Kids", desc: "Rimborso spese mediche infortunio, indennità abbonamenti non goduti, assistenza Kids.", blocked: kidsBlocked, blockedMsg: "Limite permanenza Kids: 18 anni (PDF pag. 11)" },
                          { id: "dental", label: "Pacchetto Dental", desc: "Visite odontoiatriche, detartrasi e spese a seguito di infortunio.", blocked: false, blockedMsg: "" },
                          { id: "fisio", label: "Pacchetto Fisio", desc: "Indennità intervento infortunio, immobilizzazione, riabilitazione.", blocked: false, blockedMsg: "" },
                        ].map((sez) => {
                          const checked = cfg.target_sezioni.includes(sez.id);
                          return (
                            <label
                              key={sez.id}
                              className={`flex items-start gap-3 p-3 rounded-xl border-2 transition-all ${checked ? "border-[#163321] bg-[#163321]/5" : "border-[#E5E2DB] bg-white hover:border-[#163321]/40"} ${sez.blocked ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                              data-testid={`ris-target-sez-${sez.id}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={sez.blocked}
                                onChange={() => toggleTargetSezione(sez.id)}
                                className="mt-1 accent-[#163321]"
                                data-testid={`ris-target-sez-${sez.id}-input`}
                              />
                              <div>
                                <p className="text-sm font-medium text-[#0F1F14]">{sez.label}</p>
                                <p className="text-xs text-[#4A5E51]">{sez.desc}</p>
                                {sez.blocked && (
                                  <p className="text-[10px] text-rose-700 mt-1">{sez.blockedMsg}</p>
                                )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                      {targetBlocked && (
                        <p className="text-[11px] text-rose-700 mt-2 flex items-center gap-1" data-testid="ris-target-blocked-msg">
                          <AlertTriangle className="w-3 h-3" /> Età &gt; 74: oltre il limite di ingresso Formula Target (PDF pag. 11).
                        </p>
                      )}
                    </div>
                  )}

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-[12px] text-blue-900">
                    <strong>Nota:</strong> il consulente assicurativo conferma la quotazione definitiva dopo verifica del questionario sanitario e dell'idoneità contrattuale.
                  </div>
                </div>
              );
            })()}

            {/* ============ STEP 5 — Coperture ============ */}
            {step === 5 && d.configurazione.prodotto_tipo === "target" && (
              <div className="space-y-5" data-testid="ris-step5-target-skip">
                <h2 className="font-heading text-lg text-[#0F1F14] font-medium">Configurazione coperture</h2>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-900 flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium mb-1">Configurazione Target completata</p>
                    <p className="text-[12px]">
                      Hai scelto la <strong>Formula Target</strong> ({d.configurazione.target_sezioni.map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(", ")}).
                      I pacchetti Target hanno <strong>premio flat</strong> (non dipendente da età, zona o massimale). Nessuna ulteriore configurazione è richiesta.
                      Procedi al prossimo step.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {step === 5 && d.configurazione.prodotto_tipo === "rsm" && (
              <div className="space-y-5">
                <h2 className="font-heading text-lg text-[#0F1F14] font-medium">Configurazione coperture</h2>

                <div>
                  <h3 className="font-medium text-sm text-[#0F1F14] mb-2">Opzione base</h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <OptionCard active={d.configurazione.opzione === "comfort"} onClick={() => upd("configurazione.opzione", "comfort")} title="Comfort" desc="Massimali standard, rete di strutture convenzionate. Ricovero, intervento, pre/post ricovero, indennit&agrave; sostitutiva." icon={Heart} testid="ris-opt-comfort" />
                    <OptionCard active={d.configurazione.opzione === "deluxe"} onClick={() => upd("configurazione.opzione", "deluxe")} title="Deluxe" desc="Massimali maggiorati e accesso premium al network. Tutto Comfort + servizi premium." icon={Sparkles} badge="TOP" testid="ris-opt-deluxe" />
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-sm text-[#0F1F14] mb-1">Area Extra Ricovero (opzionale)</h3>
                  <p className="text-xs text-[#4A5E51] mb-2">Scegli <strong>UNA</strong> sola opzione tra le tre. Le opzioni sono mutuamente esclusive.</p>
                  <div className="space-y-2" data-testid="ris-extra-ricovero-section">
                    {[
                      { id: "alta_diagnostica", label: "Alta Diagnostica", desc: "TAC, RMN, PET, scintigrafie e altre indagini ad alta specializzazione." },
                      { id: "diagnostica_completa", label: "Diagnostica Completa", desc: "Pacchetto ampio: diagnostica strumentale, analisi cliniche, accertamenti." },
                      { id: "visite_specialistiche", label: "Visite Specialistiche e Accertamenti Diagnostici", desc: "Visite mediche specialistiche fuori ricovero (cardiologia, ortopedia, ecc.)." },
                    ].map((g) => {
                      const checked = d.configurazione.opzione_extraricovero_selezionata === g.id;
                      return (
                        <label key={g.id} className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${checked ? "border-[#163321] bg-[#163321]/5" : "border-[#E5E2DB] bg-white hover:border-[#163321]/40"}`} data-testid={`ris-garanzia-${g.id}`}>
                          <input
                            type="radio"
                            name="opzione_extraricovero"
                            checked={checked}
                            onChange={() => selectOpzioneExtra(g.id)}
                            onClick={() => { if (checked) selectOpzioneExtra(g.id); }}
                            className="mt-1 accent-[#163321]"
                            data-testid={`ris-radio-${g.id}`}
                          />
                          <div>
                            <p className="text-sm font-medium text-[#0F1F14]">{g.label}</p>
                            <p className="text-xs text-[#4A5E51]">{g.desc}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  {/* Box info STATICO post-selezione (Task 11E.1 + 11E.2 — 2026-05-11)
                      Rete strutture e franchigia sono INFORMATIVE: il cliente sceglie
                      al momento del sinistro. Le franchigie B-H sono solo su RSM.
                      Massimali da Tariffa RiS 2023 pag. 18-19. */}
                  {d.configurazione.opzione_extraricovero_selezionata && (
                    <div className="mt-3 bg-[#F5F4F0] border border-[#E5E2DB] rounded-xl p-3 space-y-3" data-testid="ris-extra-info-panel">
                      <div className="flex items-start gap-2">
                        <Info className="w-4 h-4 text-[#163321] mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-[#0F1F14]">
                          <p className="font-medium mb-0.5">Rete di strutture sanitarie</p>
                          <ul className="text-[11px] text-[#4A5E51] space-y-0.5 ml-3 list-disc">
                            <li><strong>Network convenzionato</strong> — Strutture in rete Reale Mutua</li>
                            <li><strong>Extra-network</strong> — Libera scelta della struttura</li>
                          </ul>
                          <p className="text-[10px] text-[#4A5E51] italic mt-1">
                            Il cliente sceglie autonomamente al momento del sinistro.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-2 border-t border-[#E5E2DB] pt-2">
                        <ShieldCheck className="w-4 h-4 text-[#163321] mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-[#0F1F14]">
                          <p className="font-medium mb-0.5">Franchigia annua</p>
                          <p className="text-[11px] text-[#4A5E51]">
                            Nessuna franchigia applicata a questa sezione. Le franchigie B-H sono
                            disponibili solo nel <strong>Rimborso Spese Mediche</strong>.
                          </p>
                        </div>
                      </div>

                      {/* Tabella massimali e scoperti ufficiali pag. 18-19 */}
                      <div className="border-t border-[#E5E2DB] pt-2">
                        <p className="text-[10px] uppercase tracking-wide text-[#4A5E51] mb-1 font-medium">
                          Dettaglio franchigie e massimali — {(() => {
                            const sel = d.configurazione.opzione_extraricovero_selezionata;
                            return sel === "alta_diagnostica" ? "Alta Diagnostica"
                              : sel === "diagnostica_completa" ? "Diagnostica Completa"
                              : "Visite Specialistiche";
                          })()}
                        </p>
                        {(() => {
                          const sel = d.configurazione.opzione_extraricovero_selezionata;
                          // Dati ufficiali Tariffa RiS 2023 pag. 18-19
                          const data = {
                            alta_diagnostica: {
                              conv: "Franchigia € 50",
                              non_conv_c: "Scoperto 30% min € 50",
                              non_conv_d: "Scoperto 10% min € 50",
                              max_c: "€ 3.000/anno",
                              max_d: "€ 6.000/anno",
                              pag: "18",
                            },
                            diagnostica_completa: {
                              conv: "Franchigia € 35",
                              non_conv_c: "Scoperto 30% min € 35",
                              non_conv_d: "Scoperto 10% min € 35",
                              max_c: "€ 4.000/anno",
                              max_d: "€ 8.000/anno",
                              pag: "18",
                            },
                            visite_specialistiche: {
                              conv: "Franchigia € 35",
                              non_conv_c: "Scoperto 30% min € 35",
                              non_conv_d: "Scoperto 10% min € 35",
                              max_c: "€ 5.000/anno",
                              max_d: "€ 10.000/anno",
                              pag: "19",
                            },
                          }[sel];
                          if (!data) return null;
                          return (
                            <>
                              <table className="w-full text-[10px] border-collapse" data-testid={`ris-extra-massimali-${sel}`}>
                                <thead className="bg-white">
                                  <tr>
                                    <th className="text-left p-1 border border-[#E5E2DB] font-medium">Rete</th>
                                    <th className="text-left p-1 border border-[#E5E2DB] font-medium">Comfort</th>
                                    <th className="text-left p-1 border border-[#E5E2DB] font-medium">Deluxe</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td className="p-1 border border-[#E5E2DB]">Convenzionato</td>
                                    <td className="p-1 border border-[#E5E2DB]" colSpan="2">{data.conv}</td>
                                  </tr>
                                  <tr>
                                    <td className="p-1 border border-[#E5E2DB]">Non convenzionato</td>
                                    <td className="p-1 border border-[#E5E2DB]">{data.non_conv_c}</td>
                                    <td className="p-1 border border-[#E5E2DB]">{data.non_conv_d}</td>
                                  </tr>
                                  <tr className="bg-white">
                                    <td className="p-1 border border-[#E5E2DB] font-medium">Massimale annuo</td>
                                    <td className="p-1 border border-[#E5E2DB] font-medium">{data.max_c}</td>
                                    <td className="p-1 border border-[#E5E2DB] font-medium">{data.max_d}</td>
                                  </tr>
                                </tbody>
                              </table>
                              <p className="text-[9px] text-[#4A5E51] italic mt-1">
                                Fonte: Tariffa RiS 2023 pag. {data.pag}
                              </p>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="font-medium text-sm text-[#0F1F14] mb-1">Franchigia Rimborso Spese Mediche (RSM)</h3>
                  <p className="text-xs text-[#4A5E51] mb-2">
                    Vuoi applicare una franchigia al Rimborso Spese Mediche? Una franchigia più alta riduce il premio
                    in cambio di un importo a tuo carico per ciascun sinistro.
                  </p>
                  <select
                    value={d.configurazione.franchigia_rsm_id || "no_franchigia"}
                    onChange={(e) => upd("configurazione.franchigia_rsm_id", e.target.value)}
                    className="w-full h-9 rounded-lg border-2 border-[#E5E2DB] bg-white px-3 text-sm focus:border-[#163321] outline-none"
                    data-testid="ris-franchigia-rsm-select"
                  >
                    <option value="no_franchigia">NO FRANCHIGIA</option>
                    <option value="B">Franchigia B - Network: 1000 / Fuori Network: 1500</option>
                    <option value="C">Franchigia C - Network: 1500 / Fuori Network: 2000</option>
                    <option value="D">Franchigia D - Network: 2000 / Fuori Network: 2500</option>
                    <option value="E">Franchigia E - Network: 2500 / Fuori Network: 3000</option>
                    <option value="F">Franchigia F - Network: 3000 / Fuori Network: 3500</option>
                    <option value="G">Franchigia G - Network: 3500 / Fuori Network: 4000</option>
                    <option value="H">Franchigia H - Network: 5000 / Fuori Network: 5500</option>
                  </select>
                  <p className="text-[11px] text-[#4A5E51] mt-1.5 leading-snug">
                    Network / Fuori Network: il cliente può sempre scegliere in autonomia al momento del sinistro
                    se rivolgersi al circuito convenzionato o meno. I valori indicati sono le franchigie a carico
                    del cliente per ciascun sinistro, entro il massimale RSM (Comfort €300.000 / Deluxe €500.000).
                  </p>
                </div>

                <div>
                  <h3 className="font-medium text-sm text-[#0F1F14] mb-2">Opzioni di sconto premio</h3>
                  <div className="space-y-2">
                    <label className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer ${d.configurazione.franchigia_ricovero ? "border-[#163321] bg-[#163321]/5" : "border-[#E5E2DB] bg-white"}`} data-testid="ris-franchigia">
                      <Checkbox checked={d.configurazione.franchigia_ricovero} onCheckedChange={(v) => upd("configurazione.franchigia_ricovero", !!v)} className="mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-[#0F1F14]">Franchigia ricovero</p>
                        <p className="text-xs text-[#4A5E51]">Riduce il premio in cambio di una franchigia in caso di ricovero.</p>
                      </div>
                    </label>
                    <label className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer ${d.configurazione.sconto_salute ? "border-[#163321] bg-[#163321]/5" : "border-[#E5E2DB] bg-white"}`} data-testid="ris-sconto-salute">
                      <Checkbox checked={d.configurazione.sconto_salute} onCheckedChange={(v) => upd("configurazione.sconto_salute", !!v)} className="mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-[#0F1F14]">Sconto Salute</p>
                        <p className="text-xs text-[#4A5E51]">Sconto per assicurati con stili di vita virtuosi (verifica in fase emissione).</p>
                      </div>
                    </label>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-sm text-[#0F1F14] mb-2">Frazionamento del premio</h3>
                  <RadioGroup value={d.configurazione.frazionamento} onValueChange={(v) => upd("configurazione.frazionamento", v)} className="grid grid-cols-3 gap-2">
                    {[{ v: "annuale", l: "Annuale" }, { v: "semestrale", l: "Semestrale" }, { v: "trimestrale", l: "Trimestrale" }].map((f) => (
                      <label key={f.v} className={`flex items-center gap-2 p-3 rounded-xl border-2 cursor-pointer text-sm ${d.configurazione.frazionamento === f.v ? "border-[#163321] bg-[#163321]/5" : "border-[#E5E2DB] bg-white"}`} data-testid={`ris-fraz-${f.v}`}>
                        <RadioGroupItem value={f.v} />
                        <span>{f.l}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>
              </div>
            )}

            {/* ============ STEP 6 — Riepilogo + consensi ============ */}
            {step === 6 && (
              <div className="space-y-5">
                <h2 className="font-heading text-lg text-[#0F1F14] font-medium">Riepilogo e consensi</h2>

                <div className="bg-[#F5F4F0] rounded-xl p-4 text-sm space-y-1.5" data-testid="ris-riepilogo">
                  <p><strong>{d.anagrafica.nome} {d.anagrafica.cognome}</strong> — CF {d.anagrafica.codice_fiscale}</p>
                  <p>{d.anagrafica.email} · {d.anagrafica.telefono}</p>
                  <p>{d.anagrafica.citta} ({d.anagrafica.provincia})</p>
                  <p className="pt-1 border-t border-[#E5E2DB] mt-2">Nucleo: <strong>{d.nucleo.composizione}</strong>{d.nucleo.composizione === "famiglia" && <> · {d.nucleo.figli.length} figli</>}</p>
                  <p>Prodotto: <strong>{
                    d.configurazione.prodotto_tipo === "target"
                      ? `Target (${d.configurazione.target_sezioni.map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(", ") || "—"})`
                      : (d.configurazione.durata_rsm === "vita_intera"
                          ? "RSM Vita Intera"
                          : d.configurazione.durata_rsm === "poliennale"
                            ? "RSM Durata Definita Poliennale"
                            : "RSM Durata Definita Annuale")
                  }</strong>{d.configurazione.prodotto_tipo === "rsm" && <> · Opzione <strong>{d.configurazione.opzione === "comfort" ? "Comfort" : "Deluxe"}</strong></>}</p>
                  {d.configurazione.prodotto_tipo === "rsm" && d.configurazione.opzione_extraricovero_selezionata && (
                    <p>
                      Area Extra Ricovero: <strong>
                        {d.configurazione.opzione_extraricovero_selezionata === "alta_diagnostica" && "Alta Diagnostica"}
                        {d.configurazione.opzione_extraricovero_selezionata === "diagnostica_completa" && "Diagnostica Completa"}
                        {d.configurazione.opzione_extraricovero_selezionata === "visite_specialistiche" && "Visite Specialistiche"}
                      </strong>
                    </p>
                  )}
                  <p>Frazionamento: {d.configurazione.frazionamento}{d.configurazione.franchigia_ricovero && " · franchigia ricovero"}{d.configurazione.sconto_salute && " · sconto salute"}{d.configurazione.franchigia_rsm_id && d.configurazione.franchigia_rsm_id !== "no_franchigia" && ` · franchigia RSM: ${d.configurazione.franchigia_rsm_id}`}</p>
                </div>

                <div>
                  <Label>Note libere (opzionale)</Label>
                  <Textarea value={d.note_cliente} onChange={(e) => upd("note_cliente", e.target.value)} maxLength={2000} placeholder="Indica eventuali esigenze particolari, orari preferiti per il contatto, ecc." data-testid="ris-note" />
                </div>

                {/* Periodi di carenza (Task 11E.4 — Tariffa RiS 2023 pag. 64) */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-[12px] text-blue-900 space-y-2" data-testid="ris-carenze-info">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium mb-1">Periodi di carenza</p>
                      <ul className="space-y-0.5 ml-3 list-disc text-[11px]">
                        <li><strong>Infortuni</strong>: 6 mesi dalla data di effetto</li>
                        <li><strong>Malattie</strong> (non oncologiche): 18 mesi dalla data di effetto</li>
                        <li><strong>Malattie oncologiche</strong>: 36 mesi dalla data di effetto</li>
                        <li><strong>Parto</strong>: 10 mesi dalla data di effetto</li>
                      </ul>
                      <p className="text-[10px] italic mt-1.5 text-blue-700">Fonte: Tariffa RiS 2023 pag. 64</p>
                    </div>
                  </div>
                  <label className="flex items-start gap-2 cursor-pointer pt-1.5 border-t border-blue-200" data-testid="ris-presa-visione-carenze">
                    <Checkbox checked={d.consensi.presa_visione_carenze || false} onCheckedChange={(v) => upd("consensi.presa_visione_carenze", !!v)} className="mt-0.5" />
                    <span className="text-[11px]">Ho preso visione dei periodi di carenza applicati al prodotto (raccomandato per compliance, non vincolante per il preventivo).</span>
                  </label>
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-[12px] text-emerald-900 space-y-1.5">
                  <p className="flex items-start gap-2"><Shield className="w-4 h-4 shrink-0 mt-0.5" /><span><strong>Tutela dati sanitari (art. 9 GDPR).</strong> I dati del questionario sanitario vengono cifrati con <strong>AES-256-GCM</strong> al salvataggio. Accesso riservato al consulente assicurativo e al DPO designato, con audit log immutabile. Conservazione 5 anni dalla chiusura della richiesta.</span></p>
                </div>

                <div className="space-y-2.5">
                  <label className="flex items-start gap-2.5 cursor-pointer" data-testid="ris-consenso-privacy">
                    <Checkbox checked={d.consensi.privacy} onCheckedChange={(v) => upd("consensi.privacy", !!v)} className="mt-0.5" />
                    <span className="text-[12px] text-[#4A5E51] leading-relaxed">Dichiaro di aver letto l'<Link to="/privacy" target="_blank" className="text-[#163321] underline">Informativa Privacy</Link> e i contatti del <Link to="/privacy/dpo" target="_blank" className="text-[#163321] underline">DPO</Link>. Acconsento al trattamento dei dati per la presente richiesta di preventivo. *</span>
                  </label>
                  <label className="flex items-start gap-2.5 cursor-pointer" data-testid="ris-consenso-art9">
                    <Checkbox checked={d.consensi.dati_sanitari_informativa} onCheckedChange={(v) => upd("consensi.dati_sanitari_informativa", !!v)} className="mt-0.5" />
                    <span className="text-[12px] text-[#4A5E51] leading-relaxed">Confermo di aver letto l'<strong>informativa rinforzata art. 9 GDPR</strong> sui dati sanitari (cifratura AES-256, accesso ristretto al consulente e DPO, conservazione 5 anni, diritti GDPR). *</span>
                  </label>
                  <label className="flex items-start gap-2.5 cursor-pointer bg-blue-50 border border-blue-200 rounded-xl p-3" data-testid="ris-consenso-art9-trattamento">
                    <Checkbox checked={d.questionario_sanitario.consenso_art9_gdpr} onCheckedChange={(v) => upd("questionario_sanitario.consenso_art9_gdpr", !!v)} className="mt-0.5" />
                    <span className="text-[12px] text-blue-900 leading-relaxed"><strong>Acconsento al trattamento dei dati sanitari ai sensi dell'art. 9 GDPR</strong> per la valutazione del rischio assicurativo e l'elaborazione del preventivo. *</span>
                  </label>
                  <label className="flex items-start gap-2.5 cursor-pointer" data-testid="ris-consenso-comm">
                    <Checkbox checked={d.consensi.commerciale} onCheckedChange={(v) => upd("consensi.commerciale", !!v)} className="mt-0.5" />
                    <span className="text-[12px] text-[#4A5E51] leading-relaxed">Consento al trattamento per finalit&agrave; di marketing diretto e comunicazioni commerciali su prodotti assicurativi (opzionale).</span>
                  </label>
                  <label className="flex items-start gap-2.5 cursor-pointer" data-testid="ris-consenso-prof">
                    <Checkbox checked={d.consensi.profilazione} onCheckedChange={(v) => upd("consensi.profilazione", !!v)} className="mt-0.5" />
                    <span className="text-[12px] text-[#4A5E51] leading-relaxed">Consento alla profilazione per offerte personalizzate (opzionale).</span>
                  </label>
                </div>

                {/* FASE K2 LEGACY iter 69 — Opt-in creazione account cliente (GDPR opt-in esplicito, default deselezionato) */}
                <div className="pt-2">
                  <AccountOptInCheckbox
                    value={!!d.crea_account_ok}
                    onChange={(v) => upd("crea_account_ok", v)}
                    tid="ris-opt-in-crea-account"
                  />
                </div>

                <PrivacyLinks />
              </div>
            )}
          </Card>

          {/* v1.72.3 — Preferenze ricontatto + Accetta tutto (solo ultimo step Consensi) */}
          {step === STEPS.length - 1 && (
            <div className="mt-4 bg-white rounded-2xl border border-[#E5E2DB] p-5">
              <p className="text-xs uppercase tracking-wide text-[#4A5E51] mb-3">Come preferisci essere ricontattato/a?</p>
              <ContattoPreferenzeBlock
                value={preferenzeRicontatto}
                onChange={setPreferenzeRicontatto}
                testidPrefix="ris-ricontatto"
              />
              <div className="mt-5">
                <AcceptAllInformative
                  onAcceptAll={handleAcceptAllInformative}
                  testid="ris-accept-all-informative"
                />
              </div>
            </div>
          )}

          {/* v1.56 (feb-2026) — Doppia checkbox IVASS pre-submit (solo ultimo step Consensi) */}
          {step === STEPS.length - 1 && (
            <div className="mt-4 bg-white rounded-2xl border border-[#E5E2DB] p-5">
              <IVASSComplianceCheckboxes
                signedDeclaration={signedDeclaration}
                onSignedDeclarationChange={setSignedDeclaration}
                mupSeen={mupSeen}
                onMupSeenChange={setMupSeen}
                mupFilename={mupFilename}
                onMupFilenameChange={setMupFilename}
                testIdPrefix="ris"
              />
              {/* v1.66 — Consensi granulari GDPR (ramo salute: dati_sanitari OBBLIGATORIO ex art. 9 GDPR) */}
              <div className="mt-4">
                <ConsensiGranulari
                  value={consensi}
                  onChange={setConsensi}
                  testIdPrefix="ris"
                  includeSanitario={true}
                />
              </div>
            </div>
          )}

          <FormNavigation
            step={step}
            totalSteps={STEPS.length}
            onPrev={prev}
            onNext={next}
            onSubmit={submit}
            submitting={submitting}
            canSubmit={d.consensi.privacy && d.consensi.dati_sanitari_informativa && d.questionario_sanitario.consenso_art9_gdpr && isIvassComplianceOk({ signedDeclaration, mupSeen }) && isConsensiOk(consensi, { includeSanitario: true })}
            testIdPrefix="ris"
          />

          <div className="mt-6 text-center">
            <Link to="/polizza-salute" className="text-xs text-[#4A5E51] hover:text-[#163321] underline inline-flex items-center gap-1">
              <Stethoscope className="w-3.5 h-3.5" /> Maggiori informazioni sul prodotto RealmenteInSalute
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
